import { useState, useRef, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { useTheme } from '@/hooks/useTheme'
import { useTalents } from '@/hooks/useTalents'
import { useSelectedTalent } from '@/hooks/useSelectedTalent'
import { getAvatarGradient } from '@/lib/talent-utils'
import { clearToken } from '@/lib/auth'
import { Sidebar } from './Sidebar'
import styles from './Layout.module.css'

const PAGE_TITLES: Record<string, string> = {
  '/': '대시보드',
  '/settings': '설정',
}

export default function Layout() {
  const { pathname } = useLocation()
  const { isDark, toggle } = useTheme()
  const { talents } = useTalents()
  const { selectedTalentId, setSelectedTalentId } = useSelectedTalent()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  function handleLogout() {
    clearToken()
    window.location.href = '/login'
  }

  useEffect(() => {
    if (!dropdownOpen) return
    function handleOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [dropdownOpen])

  const selectedTalent = talents.find((t) => t.id === selectedTalentId)

  let title = PAGE_TITLES[pathname]
  if (!title) {
    if (pathname.includes('/sources')) title = '소스 취합'
    else if (pathname.includes('/resumes')) title = '이력서 스튜디오'
    else if (pathname.includes('/notion')) title = 'Notion 연동'
    else if (pathname.includes('/job-matches')) title = '채용 매칭'
    else if (pathname.includes('/cover-letters')) title = '맞춤 자소서'
    else if (pathname.includes('/portfolios')) title = '포트폴리오'
    else title = 'Career Studio'
  }

  const isStudioRoute =
    pathname.includes('/resumes') ||
    pathname.includes('/portfolios') ||
    pathname.includes('/notion')

  return (
    <div className={styles.shell}>
      <Sidebar />
      <div className={styles.mainWrap}>
        <header className={styles.header}>
          <h1 className={styles.headerTitle}>{title}</h1>
          <div className={styles.headerRight}>
            <div className={styles.talentSwitcher} ref={dropdownRef}>
              <button
                className={styles.talentBtn}
                onClick={() => setDropdownOpen((v) => !v)}
                aria-label="인재 전환"
              >
                <span
                  className={styles.talentAvatar}
                  style={{
                    background: selectedTalent
                      ? getAvatarGradient(selectedTalent.name)
                      : undefined,
                  }}
                >
                  {selectedTalent
                    ? (selectedTalent.avatarInitial ?? selectedTalent.name.charAt(0))
                    : '?'}
                </span>
                <span className={styles.talentName}>
                  {selectedTalent?.name ?? '인재 선택'}
                </span>
                <span className={styles.talentChevron}>▼</span>
              </button>
              {dropdownOpen && (
                <div className={styles.talentDropdown}>
                  {talents.length === 0 && (
                    <div className={styles.talentDropdownEmpty}>인재가 없습니다</div>
                  )}
                  {talents.map((talent) => (
                    <button
                      key={talent.id}
                      className={`${styles.talentOption} ${
                        talent.id === selectedTalentId ? styles.talentOptionActive : ''
                      }`}
                      onClick={() => {
                        setSelectedTalentId(talent.id)
                        setDropdownOpen(false)
                      }}
                    >
                      <span
                        className={styles.talentAvatar}
                        style={{ background: getAvatarGradient(talent.name) }}
                      >
                        {talent.avatarInitial ?? talent.name.charAt(0)}
                      </span>
                      <span className={styles.talentOptionInfo}>
                        <span className={styles.talentOptionName}>{talent.name}</span>
                        {talent.targetRole && (
                          <span className={styles.talentOptionRole}>
                            {talent.targetRole}
                          </span>
                        )}
                      </span>
                      {talent.id === selectedTalentId && (
                        <span className={styles.talentCheck}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className={styles.themeBtn}
              onClick={toggle}
              aria-label="테마 전환"
            >
              {isDark ? '☀' : '☾'}
            </button>
            <button
              className={styles.logoutBtn}
              onClick={handleLogout}
              aria-label="로그아웃"
              title="로그아웃"
            >
              ⏻
            </button>
          </div>
        </header>
        <main className={isStudioRoute ? styles.mainFullBleed : styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
